/**
 * Gestione dei gruppi WhatsApp sui numeri Standard (WAHA).
 * Endpoint dalla doc ufficiale (https://waha.devlike.pro/docs/how-to/groups/, verificata
 * il 26/09/2026; tutti supportati dal motore NOWEB secondo la matrice in how-to/engines/).
 * Le chiamate passano dal proxy waha-proxy (credenziali server-side, sessione controllata
 * contro l'organizzazione). Sui numeri Ufficiale (Cloud) i gruppi non esistono: non usare.
 */
import { callWahaProxy } from '../wahaProxyService';

export type GroupRole = 'superadmin' | 'admin' | 'participant' | 'left';

export interface GroupMember {
  /** Id cosi' come lo da' WAHA (serve per le azioni: puo' essere @c.us o @lid). */
  id: string;
  /** Numero in forma @c.us se noto (per mostrare il numero e abbinarlo al CRM). */
  phoneJid: string | null;
  role: GroupRole;
  /** Nome dato da WhatsApp, se presente. */
  name?: string;
}

export interface GroupDetails {
  id: string;
  subject: string;
  description: string;
  owner: string | null;
  participants: GroupMember[];
}

type Json = Record<string, unknown>;

// ---------- Logica pura (testata) ----------

export function groupsBase(session: string): string {
  return `/api/${encodeURIComponent(session)}/groups`;
}

export function groupPath(session: string, groupId: string, suffix = ''): string {
  return `${groupsBase(session)}/${encodeURIComponent(groupId)}${suffix}`;
}

/** Cifre o jid -> jid accettato da WAHA. Un @lid resta com'e'. */
export function toParticipantJid(value: string): string {
  const v = (value || '').trim();
  if (v.includes('@')) return v;
  return `${v.replace(/\D/g, '')}@c.us`;
}

/** Corpo comune di add/remove/promote/demote: { participants: [{ id }] }, senza doppioni. */
export function participantsBody(ids: string[]): { participants: Array<{ id: string }> } {
  const unique = [...new Set(ids.map(toParticipantJid).filter((j) => j.length > 5))];
  return { participants: unique.map((id) => ({ id })) };
}

export function createGroupBody(name: string, ids: string[]): { name: string; participants: Array<{ id: string }> } {
  return { name: name.trim(), participants: participantsBody(ids).participants };
}

function serialized(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const s = (v as Json)._serialized;
    if (typeof s === 'string') return s;
  }
  return '';
}

/** Ruolo da v2 (role) o dal formato vecchio (admin: 'admin'|'superadmin', isAdmin, isSuperAdmin). */
export function roleOf(p: Json): GroupRole {
  const r = typeof p.role === 'string' ? p.role : typeof p.admin === 'string' ? p.admin : '';
  if (r === 'superadmin' || p.isSuperAdmin === true) return 'superadmin';
  if (r === 'admin' || p.isAdmin === true) return 'admin';
  if (r === 'left') return 'left';
  return 'participant';
}

export function parseParticipants(raw: unknown): GroupMember[] {
  if (!Array.isArray(raw)) return [];
  const out: GroupMember[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const p = item as Json;
    const id = serialized(p.id) || serialized(p.jid);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const candidates = [p.pn, p.phoneNumber, p.jid, p.id].map(serialized);
    const phoneJid = candidates.find((c) => c.endsWith('@c.us') || c.endsWith('@s.whatsapp.net')) || null;
    const role = roleOf(p);
    if (role === 'left') continue;
    const name = [p.name, p.pushname, p.notify].find((n): n is string => typeof n === 'string' && !!n.trim());
    out.push({ id, phoneJid: phoneJid ? phoneJid.replace('@s.whatsapp.net', '@c.us') : null, role, name });
  }
  return out;
}

export function parseGroupDetails(raw: unknown, fallbackId: string): GroupDetails {
  const g = (raw && typeof raw === 'object' ? raw : {}) as Json;
  const pick = (...keys: string[]) => keys.map((k) => g[k]).find((v): v is string => typeof v === 'string') || '';
  return {
    id: serialized(g.id) || fallbackId,
    subject: pick('subject', 'name'),
    description: pick('description', 'desc'),
    owner: serialized(g.owner) || serialized(g.ownerJid) || null,
    participants: parseParticipants(g.participants),
  };
}

/** Link d'invito dalla risposta di invite-code (stringa, JSON stringa o oggetto). */
export function parseInviteLink(raw: unknown): string | null {
  let v: unknown = raw;
  if (typeof v === 'string') {
    const t = v.trim();
    try { v = JSON.parse(t); } catch { v = t; }
  }
  if (v && typeof v === 'object') {
    const o = v as Json;
    v = o.url ?? o.inviteUrl ?? o.link ?? o.code ?? o.inviteCode ?? '';
  }
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return /^[A-Za-z0-9_-]{6,}$/.test(s) ? `https://chat.whatsapp.com/${s}` : null;
}

/** Numeri non aggiunti (risposta di participants/add, se WAHA riporta un esito per numero). */
export function failedAdds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Json => !!r && typeof r === 'object')
    .filter((r) => {
      const code = String(r.status ?? r.code ?? r.error ?? '200');
      return code !== '200' && code !== 'ok' && code !== 'true';
    })
    .map((r) => serialized(r.id) || serialized(r.jid))
    .filter(Boolean);
}

/** Messaggio per l'utente da un errore WAHA. */
export function groupErrorMessage(status: number, body: string): string {
  const b = (body || '').toLowerCase();
  if (status === 501) return 'Questa funzione non è disponibile sul collegamento WhatsApp di questo numero.';
  if (status === 403 || b.includes('not-authorized') || b.includes('forbidden') || b.includes('not admin')) {
    return 'WhatsApp ha rifiutato la modifica: il tuo numero non è amministratore del gruppo.';
  }
  if (status === 404 || b.includes('item-not-found')) return 'Gruppo non trovato: forse il tuo numero non ne fa più parte.';
  if (status === 422 || status === 400) return 'Dati non validi: controlla i numeri inseriti.';
  return 'Operazione non riuscita. Riprova tra poco.';
}

export class GroupActionError extends Error {}

// ---------- Chiamate ----------

async function call(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<unknown> {
  const res = await callWahaProxy({ method, path, body });
  const text = await res.text().catch(() => '');
  if (!res.ok) throw new GroupActionError(groupErrorMessage(res.status, text));
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

export async function getGroup(session: string, groupId: string): Promise<GroupDetails> {
  const raw = await call('GET', groupPath(session, groupId));
  const details = parseGroupDetails(raw, groupId);
  // v2 ha ruoli affidabili su tutti i motori: se risponde, vince sui dati del gruppo.
  try {
    const v2 = parseParticipants(await call('GET', groupPath(session, groupId, '/participants/v2')));
    if (v2.length > 0) {
      const names = new Map(details.participants.map((p) => [p.id, p.name]));
      details.participants = v2.map((p) => ({ ...p, name: p.name || names.get(p.id) }));
    }
  } catch { /* si tengono i partecipanti del gruppo */ }
  return details;
}

export async function getGroupPictureUrl(session: string, groupId: string): Promise<string | null> {
  try {
    const raw = await call('GET', `${groupPath(session, groupId, '/picture')}?refresh=false`);
    const url = raw && typeof raw === 'object' ? (raw as Json).url : null;
    return typeof url === 'string' && url ? url : null;
  } catch {
    return null;
  }
}

export const setGroupSubject = (session: string, groupId: string, subject: string) =>
  call('PUT', groupPath(session, groupId, '/subject'), { subject: subject.trim() });

export const setGroupDescription = (session: string, groupId: string, description: string) =>
  call('PUT', groupPath(session, groupId, '/description'), { description: description.trim() });

export async function addGroupParticipants(session: string, groupId: string, ids: string[]): Promise<string[]> {
  return failedAdds(await call('POST', groupPath(session, groupId, '/participants/add'), participantsBody(ids)));
}

export const removeGroupParticipants = (session: string, groupId: string, ids: string[]) =>
  call('POST', groupPath(session, groupId, '/participants/remove'), participantsBody(ids));

export const promoteGroupAdmins = (session: string, groupId: string, ids: string[]) =>
  call('POST', groupPath(session, groupId, '/admin/promote'), participantsBody(ids));

export const demoteGroupAdmins = (session: string, groupId: string, ids: string[]) =>
  call('POST', groupPath(session, groupId, '/admin/demote'), participantsBody(ids));

export async function getGroupInviteLink(session: string, groupId: string): Promise<string | null> {
  return parseInviteLink(await call('GET', groupPath(session, groupId, '/invite-code')));
}

export async function revokeGroupInviteLink(session: string, groupId: string): Promise<string | null> {
  return parseInviteLink(await call('POST', groupPath(session, groupId, '/invite-code/revoke')));
}

export const leaveGroup = (session: string, groupId: string) =>
  call('POST', groupPath(session, groupId, '/leave'));

/** Crea il gruppo e ne ritorna l'id (@g.us) se WAHA lo restituisce. */
export async function createGroup(session: string, name: string, ids: string[]): Promise<string | null> {
  const raw = await call('POST', groupsBase(session), createGroupBody(name, ids));
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Json;
  const id = serialized(o.id) || serialized(o.gid) || serialized((o.groupMetadata as Json | undefined)?.id);
  return id && id.endsWith('@g.us') ? id : null;
}
